import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  InjectionToken,
  Input,
  OnChanges,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';

/**
 * Injection token for providing {@link ChangeDetectorRef} to dropdown option components.
 *
 * @remarks
 * This token enables the option component to trigger change detection on the parent dropdown
 * when its inputs change, which is necessary because the dropdown uses OnPush change detection strategy.
 *
 * @example
 *
 * ```typescript
 * // Used in dropdown component providers
 * @Component({
 *   selector: 'bb-dropdown-single-select-ui',
 *   providers: [
 *     {
 *       provide: BB_DROPDOWN_CHANGE_DETECTION_REF_TOKEN,
 *       useExisting: ChangeDetectorRef,
 *     },
 *   ],
 * })
 * export class DropdownSingleSelectComponent { }
 * ```
 */
export const BB_DROPDOWN_CHANGE_DETECTION_REF_TOKEN: InjectionToken<ChangeDetectorRef> = new InjectionToken(
  'BB_DROPDOWN_SINGLE_SELECT',
);

/**
 * Represents a single option within the dropdown single select component.
 *
 * @remarks
 * This component is used as a child of {@link DropdownSingleSelectComponent} to define individual
 * dropdown options. It supports both content projection and programmatic creation via @for loops.
 * When its inputs change, it automatically triggers change detection on the parent dropdown component
 * through the injected {@link BB_DROPDOWN_CHANGE_DETECTION_REF_TOKEN}.
 *
 * @example
 *
 * ```html
 * <bb-dropdown-single-select-ui>
 *   <bb-dropdown-single-select-option-ui
 *     label="Option One"
 *     value="1"
 *   />
 *   <bb-dropdown-single-select-option-ui
 *     label="Option Two"
 *     value="2"
 *   />
 * </bb-dropdown-single-select-ui>
 * ```
 */
@Component({
  selector: 'bb-dropdown-single-select-option-ui',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '',
  standalone: false,
})
export class DropdownSingleSelectOptionComponent implements OnChanges {
  /**
   * The value of the native option element.
   *
   * @example
   *
   * ```html
   * <bb-dropdown-single-select-option-ui
   *   label="Option One"
   *   value="1"
   * />
   * ```
   */
  @Input() value: string | undefined;
  /**
   * The label of the option item.
   *
   * @example
   *
   * ```html
   * <bb-dropdown-single-select-option-ui
   *   label="Option One"
   *   value="1"
   * />
   * ```
   */
  @Input() label: string | undefined;

  constructor(@Inject(BB_DROPDOWN_CHANGE_DETECTION_REF_TOKEN) private readonly dropdownCDRef: ChangeDetectorRef) {}

  // NOTES: trigger of detectChanges is needed
  // because DropdownSingleSelectComponent has changeDetection
  // strategy onPush, hence it reacts only to changes
  // of its own @Inputs, but not @Inputs() of options component
  /**
   * Triggers change detection on the parent dropdown when option inputs change.
   *
   * @remarks
   * This ensures the dropdown updates when option `value` or `label` properties change,
   * since the parent dropdown uses OnPush change detection strategy and doesn't automatically
   * detect changes to child component inputs.
   *
   * @param changes - Object containing the changed input properties
   * @param changes.value - Changes to the value input property
   * @param changes.label - Changes to the label input property
   */
  ngOnChanges({ value = {} as SimpleChange, label = {} as SimpleChange }: SimpleChanges) {
    const { firstChange: firstChangeValue } = value;
    const { firstChange: firstChangeLabel } = label;

    if (firstChangeLabel && firstChangeValue) {
      return;
    }

    this.dropdownCDRef.detectChanges();
  }
}
